import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { ImageUploadField } from '../components/ImageUploadField';
import { landingService } from '../services/landingService';
import { productService } from '../services/productService';
import { fieldClass, labelClass, slugify } from '../utils/form';
import type { Category } from '../types';

type LinkKind = 'product' | 'category' | 'custom';

type LandingRow = {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  body: string;
  image: string;
  badge: string;
  metaTitle: string;
  metaDescription: string;
  ctaText: string;
  ctaLink: string;
  isActive: boolean;
  path: string;
};

type FormState = {
  title: string;
  slug: string;
  subtitle: string;
  body: string;
  image: string;
  badge: string;
  metaTitle: string;
  metaDescription: string;
  ctaText: string;
  ctaLink: string;
  linkKind: LinkKind;
  productSlug: string;
  categorySlug: string;
  isActive: boolean;
};

const emptyForm = (): FormState => ({
  title: '',
  slug: '',
  subtitle: '',
  body: '',
  image: '',
  badge: '',
  metaTitle: '',
  metaDescription: '',
  ctaText: 'مشاهده و خرید',
  ctaLink: '/products',
  linkKind: 'custom',
  productSlug: '',
  categorySlug: '',
  isActive: true,
});

function detectLinkKind(link: string): { kind: LinkKind; productSlug: string; categorySlug: string } {
  const productMatch = link.match(/^\/products\/([^/?#]+)/);
  if (productMatch) return { kind: 'product', productSlug: productMatch[1], categorySlug: '' };
  const categoryMatch = link.match(/^\/categories\/([^/?#]+)/);
  if (categoryMatch) return { kind: 'category', productSlug: '', categorySlug: categoryMatch[1] };
  return { kind: 'custom', productSlug: '', categorySlug: '' };
}

function mapRow(raw: Record<string, unknown>): LandingRow {
  return {
    id: String(raw.id ?? ''),
    slug: String(raw.slug ?? ''),
    title: String(raw.title ?? ''),
    subtitle: String(raw.subtitle ?? ''),
    body: String(raw.body ?? ''),
    image: String(raw.image ?? ''),
    badge: String(raw.badge ?? ''),
    metaTitle: String(raw.metaTitle ?? ''),
    metaDescription: String(raw.metaDescription ?? ''),
    ctaText: String(raw.ctaText ?? 'مشاهده'),
    ctaLink: String(raw.ctaLink ?? '/products'),
    isActive: Boolean(raw.isActive),
    path: String(raw.path ?? `/l/${raw.slug ?? ''}`),
  };
}

function formFromRow(row: LandingRow): FormState {
  const detected = detectLinkKind(row.ctaLink);
  return {
    title: row.title,
    slug: row.slug,
    subtitle: row.subtitle,
    body: row.body,
    image: row.image,
    badge: row.badge,
    metaTitle: row.metaTitle,
    metaDescription: row.metaDescription,
    ctaText: row.ctaText,
    ctaLink: row.ctaLink,
    linkKind: detected.kind,
    productSlug: detected.productSlug,
    categorySlug: detected.categorySlug,
    isActive: row.isActive,
  };
}

function resolveCtaLink(form: FormState): string {
  if (form.linkKind === 'product' && form.productSlug) return `/products/${form.productSlug}`;
  if (form.linkKind === 'category' && form.categorySlug) return `/categories/${form.categorySlug}`;
  return form.ctaLink.trim() || '/products';
}

function payloadFromForm(form: FormState): Record<string, unknown> {
  return {
    title: form.title.trim(),
    slug: form.slug.trim() || slugify(form.title),
    subtitle: form.subtitle.trim(),
    body: form.body.trim(),
    image: form.image.trim(),
    badge: form.badge.trim(),
    metaTitle: form.metaTitle.trim(),
    metaDescription: form.metaDescription.trim(),
    ctaText: form.ctaText.trim() || 'مشاهده',
    ctaLink: resolveCtaLink(form),
    isActive: form.isActive,
  };
}

type ProductOpt = { id: string; name: string; slug: string };

export function LandingsPage() {
  const [items, setItems] = useState<LandingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LandingRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await landingService.list();
      setItems(rows.map(mapRow));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!formOpen) return;
    void (async () => {
      try {
        const [cats, prods] = await Promise.all([
          productService.getCategories(),
          productService.getProducts({ page: 1, limit: 200 }),
        ]);
        setCategories(cats);
        setProducts(
          (prods.data ?? []).map((p) => ({
            id: p.id,
            name: p.name,
            slug: String((p as { slug?: string }).slug ?? ''),
          })).filter((p) => p.slug),
        );
      } catch {
        /* selectors optional */
      }
    })();
  }, [formOpen]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setSlugTouched(false);
    setFormOpen(true);
  };

  const openEdit = (row: LandingRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setSlugTouched(true);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'title' && !slugTouched && !editing) {
        next.slug = slugify(String(value));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('عنوان الزامی است');
      return;
    }
    if (form.linkKind === 'product' && !form.productSlug) {
      toast.error('یک محصول برای دکمه انتخاب کنید');
      return;
    }
    if (form.linkKind === 'category' && !form.categorySlug) {
      toast.error('یک دسته‌بندی برای دکمه انتخاب کنید');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) await landingService.update(editing.id, payload);
      else await landingService.create(payload);
      toast.success(editing ? 'لندینگ ذخیره شد' : 'لندینگ ساخته شد');
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: LandingRow) => {
    if (!window.confirm(`لندینگ «${row.title}» حذف شود؟`)) return;
    try {
      await landingService.remove(row.id);
      toast.success('لندینگ حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const storefrontBase = (import.meta.env.VITE_STOREFRONT_URL as string | undefined)?.replace(/\/$/, '') || 'https://evagol.ir';

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">لندینگ‌ها</h1>
          <p className="text-slate-600 mt-1">
            صفحات کمپین با آدرس اختصاصی، سئو و دکمه لینک به محصول یا دسته
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          لندینگ جدید
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          در حال بارگذاری...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          هنوز لندینگی نیست. اولین صفحه کمپین را بسازید.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="admin-table-scroll">
            <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-medium">عنوان</th>
                <th className="px-4 py-3 text-right font-medium">آدرس</th>
                <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                <th className="px-4 py-3 text-right font-medium">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.title}</div>
                    {row.subtitle ? (
                      <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">{row.subtitle}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700" dir="ltr">
                      {row.path}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        row.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <a
                        href={`${storefrontBase}${row.path}`}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                        title="مشاهده در فروشگاه"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                        title="ویرایش"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(row)}
                        className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش لندینگ' : 'لندینگ جدید'}
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pe-1" dir="rtl">
          <div>
            <label className={labelClass}>عنوان</label>
            <input className={fieldClass} value={form.title} onChange={(e) => setField('title', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>اسلاگ آدرس</label>
            <div className="flex items-center gap-2" dir="ltr">
              <span className="text-xs text-slate-500">/l/</span>
              <input
                className={fieldClass}
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField('slug', e.target.value);
                }}
                placeholder="norouz-1405"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>زیرعنوان کوتاه</label>
            <input
              className={fieldClass}
              value={form.subtitle}
              onChange={(e) => setField('subtitle', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>متن صفحه</label>
            <textarea
              className={fieldClass}
              rows={5}
              value={form.body}
              onChange={(e) => setField('body', e.target.value)}
              placeholder="توضیح کمپین..."
            />
          </div>
          <div>
            <label className={labelClass}>برچسب (اختیاری)</label>
            <input
              className={fieldClass}
              value={form.badge}
              onChange={(e) => setField('badge', e.target.value)}
              placeholder="مثلاً فقط امروز"
            />
          </div>
          <ImageUploadField
            label="تصویر هیرو"
            value={form.image}
            onChange={(url) => setField('image', url)}
          />

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-800">دکمه فراخوان (CTA)</p>
            <div>
              <label className={labelClass}>متن دکمه</label>
              <input
                className={fieldClass}
                value={form.ctaText}
                onChange={(e) => setField('ctaText', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>نوع لینک</label>
              <select
                className={fieldClass}
                value={form.linkKind}
                onChange={(e) => setField('linkKind', e.target.value as LinkKind)}
              >
                <option value="product">محصول خاص</option>
                <option value="category">دسته‌بندی</option>
                <option value="custom">لینک دلخواه</option>
              </select>
            </div>
            {form.linkKind === 'product' ? (
              <div>
                <label className={labelClass}>محصول</label>
                <select
                  className={fieldClass}
                  value={form.productSlug}
                  onChange={(e) => setField('productSlug', e.target.value)}
                >
                  <option value="">انتخاب محصول...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.slug}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {form.linkKind === 'category' ? (
              <div>
                <label className={labelClass}>دسته‌بندی</label>
                <select
                  className={fieldClass}
                  value={form.categorySlug}
                  onChange={(e) => setField('categorySlug', e.target.value)}
                >
                  <option value="">انتخاب دسته...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {form.linkKind === 'custom' ? (
              <div>
                <label className={labelClass}>لینک</label>
                <input
                  className={fieldClass}
                  dir="ltr"
                  value={form.ctaLink}
                  onChange={(e) => setField('ctaLink', e.target.value)}
                  placeholder="/products?onlyDiscount=true"
                />
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-800">سئو</p>
            <div>
              <label className={labelClass}>عنوان متا (اختیاری)</label>
              <input
                className={fieldClass}
                value={form.metaTitle}
                onChange={(e) => setField('metaTitle', e.target.value)}
                placeholder="خالی = همان عنوان صفحه"
              />
            </div>
            <div>
              <label className={labelClass}>توضیح متا</label>
              <textarea
                className={fieldClass}
                rows={2}
                value={form.metaDescription}
                onChange={(e) => setField('metaDescription', e.target.value)}
                placeholder="برای گوگل؛ اگر خالی باشد از زیرعنوان/متن استفاده می‌شود"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
            فعال (در فروشگاه و نقشه سایت دیده شود)
          </label>
        </div>
      </Modal>
    </div>
  );
}
